import { useMemo } from "react";

import type { Suggestion } from "../types";
import { SuggestionItem } from "./SuggestionItem";

interface CharlieBriefProps {
  suggestions: Suggestion[];
  openBuckets: Record<string, boolean>;
  toggleBucket: (b: string) => void;
  onAccept: (sug: Suggestion, notes: string, due: string) => void;
  onReject: (sug: Suggestion) => void;
  /** The whole suggestion, not its records: live rows fetch theirs on the
   *  click, which only the root can do. */
  onRecords: (sug: Suggestion) => void;
  /** The suggestion whose drill is in flight, if any. */
  busyId?: string | null;
}

/**
 * Charlie's brief — the decision queue, grouped by signal category. A bucket
 * with a `hard` deadline still open reads red; one with nothing left reads
 * "clear ✓".
 *
 * The categories come from the data, not from a list held here: they are
 * administered in the database (`SignalCategoryName`), so a new one appears the
 * moment the SP raises a task for it, and one with nothing waiting simply never
 * renders. Order is first-seen — the SP decides what leads the brief.
 */
export function CharlieBrief({
  suggestions,
  openBuckets,
  toggleBucket,
  onAccept,
  onReject,
  onRecords,
  busyId,
}: CharlieBriefProps) {
  const buckets = useMemo(() => {
    const groups = new Map<string, { label: string; items: Suggestion[] }>();
    for (const s of suggestions) {
      const g = groups.get(s.bucket);
      if (g) g.items.push(s);
      else groups.set(s.bucket, { label: s.bucketLabel || s.bucket, items: [s] });
    }
    return Array.from(groups, ([key, g]) => ({ key, ...g }));
  }, [suggestions]);

  return (
    <section className="card">
      <div className="lbl">
        Charlie's brief — needs your decision
        <span className="n">you own every task · delegate in your notes, not the system</span>
      </div>

      {buckets.length === 0 && <div className="empty">Nothing needs your decision.</div>}

      {buckets.map(({ key, label, items }) => {
        const open = items.filter((s) => s.status === "open").length;
        const hard = items.some((s) => s.deadline === "hard" && s.status === "open");
        return (
          <div key={key} className={"bucket" + (openBuckets[key] ? " open" : "")}>
            <div className="bhead" onClick={() => toggleBucket(key)}>
              <span className="chev">▶</span>
              <span className="name">{label}</span>
              <span className={"count" + (hard ? " hard" : "") + (!open ? " clear" : "")}>
                {open ? open : "clear ✓"}
              </span>
            </div>
            <div className="items">
              {items.map((s) => (
                <SuggestionItem
                  key={s.id}
                  sug={s}
                  onAccept={onAccept}
                  onReject={onReject}
                  onRecords={() => onRecords(s)}
                  busy={busyId === s.id}
                />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}
